import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mermaid from 'mermaid';
import type { ProcessGraph, ProcessNode } from '@acde/shared';
import { HiOutlineExclamationTriangle, HiOutlineMap } from 'react-icons/hi2';

let mermaidInitialised = false;

function initialiseMermaid() {
  if (mermaidInitialised) return;

  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    securityLevel: 'loose',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    logLevel: 'error',
    themeVariables: {
      primaryColor: '#002BFE',
      primaryTextColor: '#ffffff',
      primaryBorderColor: '#002BFE',
      lineColor: '#6B7280',
      secondaryColor: '#10B981',
      tertiaryColor: '#8B5CF6',
      background: '#1F2937',
      mainBkg: '#1F2937',
      nodeBorder: '#4B5563',
      clusterBkg: '#374151',
      titleColor: '#F9FAFB',
      edgeLabelBackground: '#374151',
      fontSize: '14px',
    },
    flowchart: {
      htmlLabels: false,
      useMaxWidth: true,
      curve: 'basis',
      padding: 20,
      nodeSpacing: 60,
      rankSpacing: 60,
    },
  });

  mermaidInitialised = true;
}

function sanitiseLabel(text: string, maxLength: number = 40): string {
  if (!text) return '';
  let cleaned = text.replace(/[<>]/g, '');
  if (cleaned.length > maxLength) {
    cleaned = cleaned.substring(0, maxLength - 3) + '...';
  }
  cleaned = cleaned.replace(/"/g, '#quot;');
  cleaned = cleaned.replace(/[[\]{}()#&]/g, (char) => {
    const replacements: Record<string, string> = {
      '[': '#91;',
      ']': '#93;',
      '{': '#123;',
      '}': '#125;',
      '(': '#40;',
      ')': '#41;',
      '#': '#35;',
      '&': '#38;',
    };
    return replacements[char] || char;
  });
  return cleaned;
}

function graphToMermaid(graph: ProcessGraph): string {
  if (graph.nodes.length === 0) {
    return `flowchart TD
    A["Start your discovery interview"]:::start`;
  }

  const lines: string[] = ['flowchart TD'];

  // Create ID map
  const idMap = new Map<string, string>();
  graph.nodes.forEach((node, index) => {
    idMap.set(node.id, `n${index}`);
  });

  // Add nodes
  for (const node of graph.nodes) {
    const safeId = idMap.get(node.id) || `n${graph.nodes.indexOf(node)}`;
    const label = sanitiseLabel(node.label, 30);
    const shape = getNodeShape(node.type, safeId, label);
    const styleClass = getNodeStyleClass(node.type);
    lines.push(`    ${shape}:::${styleClass}`);
  }

  // Add edges
  for (const edge of graph.edges) {
    const sourceId = idMap.get(edge.source);
    const targetId = idMap.get(edge.target);
    if (sourceId && targetId) {
      const edgeLabel = edge.label ? `|"${sanitiseLabel(edge.label, 20)}"|` : '';
      const arrow = edge.type === 'conditional' ? '-.->' : '-->';
      lines.push(`    ${sourceId} ${arrow}${edgeLabel} ${targetId}`);
    }
  }

  // Add style definitions
  lines.push('');
  lines.push('    classDef start fill:#6B7280,stroke:#4B5563,color:#fff,stroke-width:2px');
  lines.push('    classDef process fill:#002BFE,stroke:#0020c2,color:#fff,stroke-width:2px');
  lines.push('    classDef decision fill:#F59E0B,stroke:#D97706,color:#fff,stroke-width:2px');
  lines.push('    classDef terminator fill:#10B981,stroke:#059669,color:#fff,stroke-width:2px');
  lines.push('    classDef subprocess fill:#8B5CF6,stroke:#7C3AED,color:#fff,stroke-width:2px');

  return lines.join('\n');
}

function getNodeShape(type: ProcessNode['type'], id: string, label: string): string {
  const quotedLabel = `"${label}"`;
  switch (type) {
    case 'terminator':
      return `${id}([${quotedLabel}])`;
    case 'decision':
      return `${id}{${quotedLabel}}`;
    case 'subprocess':
      return `${id}[[${quotedLabel}]]`;
    default:
      return `${id}[${quotedLabel}]`;
  }
}

function getNodeStyleClass(type: ProcessNode['type']): string {
  switch (type) {
    case 'terminator':
      return 'terminator';
    case 'decision':
      return 'decision';
    case 'subprocess':
      return 'subprocess';
    default:
      return 'process';
  }
}

interface ProcessDiagramProps {
  graph: ProcessGraph;
  onNodeClick?: (nodeId: string) => void;
  className?: string;
  isAnalyzing?: boolean;
}

export function ProcessDiagram({ graph, onNodeClick, className = '', isAnalyzing = false }: ProcessDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mermaidCode = useMemo(() => graphToMermaid(graph), [graph]);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!mermaidCode || !containerRef.current) return;

      try {
        initialiseMermaid();
        containerRef.current.innerHTML = '';
        setError(null);

        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const { svg, bindFunctions } = await mermaid.render(id, mermaidCode);
        setSvgContent(svg);

        if (containerRef.current) {
          containerRef.current.innerHTML = svg;

          if (bindFunctions) {
            bindFunctions(containerRef.current);
          }

          if (onNodeClick) {
            const nodes = containerRef.current.querySelectorAll('.node');
            nodes.forEach((node) => {
              node.addEventListener('click', () => {
                const nodeId = node.id?.replace(/^flowchart-/, '').split('-')[0];
                if (nodeId) {
                  onNodeClick(nodeId);
                }
              });
              (node as HTMLElement).style.cursor = 'pointer';
            });
          }
        }
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [mermaidCode, onNodeClick]);

  const handleCopyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(mermaidCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [mermaidCode]);

  const handleExportSvg = useCallback(() => {
    if (!svgContent) return;

    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'process-diagram.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [svgContent]);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 sm:px-3 py-2 bg-user-msg rounded-t-lg border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium text-muted shrink-0">Process Map</span>
          {graph.nodes.length > 0 && (
            <span className="text-xs text-muted bg-card px-2 py-0.5 rounded-full">
              {graph.nodes.length} steps
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopyCode}
            className="p-1.5 text-xs text-muted hover:text-foreground hover:bg-card rounded transition-colours"
            title="Copy Mermaid code"
          >
            {copied ? (
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <button
            onClick={handleExportSvg}
            className="p-1.5 text-xs text-muted hover:text-foreground hover:bg-card rounded transition-colours"
            title="Export as SVG"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Diagram */}
      <div className="flex-1 min-h-0 bg-user-msg rounded-b-lg overflow-auto p-2 sm:p-4">
        {error ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <div className="text-center max-w-md px-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                <HiOutlineExclamationTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div className="text-xs sm:text-sm font-medium text-red-500 mb-1">Diagram Error</div>
              <div className="text-xs text-muted">{error}</div>
            </div>
          </div>
        ) : graph.nodes.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <div className="text-center px-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-card flex items-center justify-center ${isAnalyzing ? 'text-primary' : 'text-muted'}`}>
                {isAnalyzing ? (
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <HiOutlineMap className="w-6 h-6 sm:w-8 sm:h-8" />
                )}
              </div>
              <div className="text-xs sm:text-sm text-muted">
                {isAnalyzing ? (
                  <>
                    Analyzing conversation...
                    <br />
                    <span className="text-xs">Extracting process steps</span>
                  </>
                ) : (
                  <>
                    Your process map will appear here
                    <br />
                    as you describe the workflow
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="flex items-center justify-center min-h-[200px] [&_svg]:max-w-full [&_svg]:h-auto"
          />
        )}
      </div>

      {/* Legend */}
      {graph.nodes.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 py-2 text-xs text-muted">
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-primary" /> Process
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-amber-500" /> Decision
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-emerald-500" /> End
          </span>
        </div>
      )}
    </div>
  );
}
